import { useEffect, useState } from "react";

/**
 * HeroStarIntro — cinematic first-visit reveal.
 *
 * Renders a large bronze Lone Star centered over the hero, then translates
 * + shrinks it to the exact position of the navbar's brand star before
 * fading out. Plays once per session (guarded by sessionStorage) and never
 * plays for users with prefers-reduced-motion.
 *
 * Timing:
 *   0ms   — enter (opacity 0, scale 0.55, centered)
 *   400ms — fully visible, scale 1.0
 *   950ms — hold
 *   950–1650ms — fly to navbar star + fade out
 *   1650ms — unmount
 */
const SESSION_KEY = "lsc-star-intro-shown";

export default function HeroStarIntro({ targetSelector = "[data-lsc-star-target]" }) {
  // "idle" (haven't decided) | "enter" (playing entrance) | "fly" (translating) | "done" (unmount)
  const [phase, setPhase] = useState("idle");
  const [target, setTarget] = useState(null); // {top,left,width,height}

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Respect reduced motion + only run once per session
    if (
      sessionStorage.getItem(SESSION_KEY) === "1" ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
    ) {
      setPhase("done");
      sessionStorage.setItem(SESSION_KEY, "1");
      return;
    }
    setPhase("enter");
    // Poll briefly for the target element (navbar mounts async)
    const started = performance.now();
    let raf;
    const findTarget = () => {
      const el = document.querySelector(targetSelector);
      if (el) {
        const r = el.getBoundingClientRect();
        setTarget({ top: r.top, left: r.left, width: r.width, height: r.height });
        // Hold for 550ms after entrance completes, then fly
        window.setTimeout(() => setPhase("fly"), 950);
        window.setTimeout(() => {
          setPhase("done");
          sessionStorage.setItem(SESSION_KEY, "1");
        }, 1650);
      } else if (performance.now() - started < 2500) {
        raf = requestAnimationFrame(findTarget);
      } else {
        // Give up — just fade out in place
        window.setTimeout(() => setPhase("done"), 1400);
        sessionStorage.setItem(SESSION_KEY, "1");
      }
    };
    raf = requestAnimationFrame(findTarget);
    return () => raf && cancelAnimationFrame(raf);
  }, [targetSelector]);

  if (phase === "idle" || phase === "done") return null;

  const flying = phase === "fly" && target;
  // Base center position (viewport center) vs. target center
  const centerTop = "50%";
  const centerLeft = "50%";
  const targetTop = flying ? `${target.top + target.height / 2}px` : centerTop;
  const targetLeft = flying ? `${target.left + target.width / 2}px` : centerLeft;
  // Compute the "final" scale so the flying star lands at the navbar star's size (140 → target.width)
  const finalScale = flying ? Math.max(0.08, target.width / 140) : 1;

  return (
    <div
      className="fixed inset-0 z-[95] pointer-events-none flex items-center justify-center"
      data-testid="hero-star-intro"
      aria-hidden="true"
      style={{
        background: flying
          ? "transparent"
          : "radial-gradient(circle at center, rgba(255,255,255,0.35), transparent 62%)",
        transition: "background 700ms ease-in",
      }}
    >
      <svg
        width="140"
        height="140"
        viewBox="0 0 100 100"
        className={`hero-star-svg ${flying ? "hero-star-fly" : "hero-star-enter"}`}
        style={{
          position: "fixed",
          top: targetTop,
          left: targetLeft,
          transform: `translate(-50%, -50%) scale(${finalScale})`,
          opacity: flying ? 0 : 1,
          transition: flying
            ? "top 720ms cubic-bezier(.7,0,.3,1), left 720ms cubic-bezier(.7,0,.3,1), transform 720ms cubic-bezier(.7,0,.3,1), opacity 560ms ease-in 160ms"
            : undefined,
          filter: "drop-shadow(0 8px 32px rgba(157,126,76,0.45))",
        }}
      >
        <defs>
          <linearGradient id="lsc-intro-star-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E4C58A" />
            <stop offset="50%" stopColor="#C9A46A" />
            <stop offset="100%" stopColor="#9E7C41" />
          </linearGradient>
        </defs>
        <polygon
          points={buildStarPoints()}
          fill="url(#lsc-intro-star-grad)"
          stroke="#7A6338"
          strokeWidth="0.5"
        />
      </svg>

      <style>{`
        @keyframes lsc-hero-star-in {
          0%   { opacity: 0;    transform: translate(-50%, -50%) scale(0.55); }
          60%  { opacity: 1;    transform: translate(-50%, -50%) scale(1.06); }
          100% { opacity: 1;    transform: translate(-50%, -50%) scale(1.00); }
        }
        .hero-star-enter { animation: lsc-hero-star-in 640ms cubic-bezier(.16,.7,.3,1) both; }
      `}</style>
    </div>
  );
}

function buildStarPoints() {
  const cx = 50, cy = 50, rOuter = 48, rInner = 20;
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? rOuter : rInner;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    pts.push(`${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(" ");
}
